import { useSafeAreaInsets } from "react-native-safe-area-context";

import { Button, FAIconName } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import { ShadowStyles } from "@/ui/theme/styles";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

type LeftButtonType = "back" | "cancel";
const LeftButtonTypeValues: Record<LeftButtonType, LeftButtonType> = {
  back: "back",
  cancel: "cancel",
};
interface ModalFooterProps extends BaseViewProps {
  isLeftButtonHidden?: boolean;
  isLoading?: boolean;
  isRightButtonDisabled?: boolean;
  leftButtonType?: LeftButtonType;
  onLeftButtonPress: () => Promise<void> | void;
  onRightButtonPress: () => Promise<void> | void;
  rightButtonLabel: string;
  rightButtonLeftIcon?: FAIconName;
  rightButtonRightIcon?: FAIconName;
  shouldPadBottom?: boolean;
}

interface LeftButtonAttributes {
  leftIcon?: FAIconName;
  title: string;
}
const getLeftButtonAttributes = (
  leftButtonType?: LeftButtonType,
): LeftButtonAttributes => {
  if (leftButtonType === "cancel") {
    return { title: "Cancel" };
  }
  return { leftIcon: FAIconName.ChevronLeft, title: "Back" };
};

const ModalFooter = ({
  isLeftButtonHidden,
  isLoading,
  isRightButtonDisabled,
  leftButtonType,
  onLeftButtonPress,
  onRightButtonPress,
  rightButtonLabel,
  rightButtonLeftIcon,
  rightButtonRightIcon,
  shouldPadBottom = true,
  testID,
}: ModalFooterProps) => {
  const insets = useSafeAreaInsets();
  const leftButtonAttributes = getLeftButtonAttributes(leftButtonType);

  const baseTestID = joinTestIDs(testID, "modal-footer");
  return (
    <VStack
      backgroundColor={ColorToken.NeutralBackgroundPrimary}
      paddingBottom={shouldPadBottom ? insets.bottom : undefined}
      paddingHorizontal={LayoutToken.ContentPaddingHorizontal}
      testID={baseTestID}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...ShadowStyles}
    >
      <HStack
        justifyContent={isLeftButtonHidden ? "flex-end" : "space-between"}
        paddingVertical={12}
      >
        {!isLeftButtonHidden && (
          <Button
            leftIcon={leftButtonAttributes.leftIcon}
            onPress={onLeftButtonPress}
            testID={joinTestIDs(baseTestID, "left")}
            title={leftButtonAttributes.title}
            variant="secondary"
          />
        )}
        <Button
          isDisabled={isRightButtonDisabled}
          isLoading={isLoading}
          leftIcon={rightButtonLeftIcon}
          onPress={onRightButtonPress}
          rightIcon={rightButtonRightIcon}
          testID={joinTestIDs(baseTestID, "primary")}
          title={rightButtonLabel}
          variant="primary"
        />
      </HStack>
    </VStack>
  );
};

export type { ModalFooterProps };
export { LeftButtonTypeValues };
export default ModalFooter;
